import { SBCAAS_CLASSIC_LABEL, SBCAAS_MODERN_LABEL } from '@/constants/sbcaas';
import AzureLogo from 'assets/images/acs.svg';
import eightByEightLogo from 'assets/images/8x8-tcap.svg';
import ringCentralLogo from 'assets/images/ring-central.svg';
import dialPadLogo from 'assets/images/dialpad.png';
import GCCHighLogo from 'assets/images/gcc-high.svg';
import zoomIcon from 'assets/images/zoom-tcap.svg';
import sbcLogo from 'assets/images/sbcaas.svg';
import SRTLogo from 'assets/images/sbcaas-v2.svg';
import msTeamsLogo from 'assets/images/teams.svg';
import teamsPhoneLogo from 'assets/images/teams-phone.svg';
import MsAppAuthLogo from 'assets/images/graph-api.svg';
import occLogo from 'assets/images/teams-occ.svg';
import WebEx_Circle from 'assets/images/webex.svg';
import {
    MICROSOFT_GRAPH,
    APP_AUTH,
    ACS,
    EIGHTBYEIGHT,
    OPERATOR_CONNECT,
    DIRECT_ROUTING,
    ZOOM,
    ZOOM_AUTH,
    EIGHTBYEIGHT_AUTH,
    SBC,
    OPERATOR_CONNECT_CONFERENCING,
    TPM,
    VENDOR_DEFINED,
    UCAAS_ACCORDION,
    GCC_HIGH,
    RINGCENTRAL,
    RINGCENTRAL_AUTH,
    DIALPAD,
    DIALPAD_AUTH,
    WEBEX,
    WEBEX_AUTH,
    VENDOR_DEFINED_ACCORDION,
    OPERATOR_PLATFORM,
    OPERATOR_PLATFORM_ACCORDION,
    SRT,
} from '../constants';
import { PlatformEnums } from 'utils/platforms';

// Data-only lookup table: every platform's dashboard-card display info in one
// place. Add a new platform here (plus, if it needs a Grant Access flow, an
// entry in services/platformRegistry.js) instead of adding a switch case.
const CARD_DETAILS_BY_SERVICE_TYPE = {
    [ACS]: {
        serviceType: ACS,
        serviceName: 'ACS Direct Routing',
        icon: AzureLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize TCAP to connect to your Azure tenancy to manage or setup an Azure Communication Services instance.',
    },
    [APP_AUTH]: {
        serviceType: APP_AUTH,
        serviceName: 'Microsoft Graph API',
        icon: MsAppAuthLogo,
    },
    [OPERATOR_CONNECT]: {
        serviceType: OPERATOR_CONNECT,
        serviceName: 'Operator Connect',
        icon: msTeamsLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize your Microsoft Teams tenancy to connect using Operator Connect API endpoints. This is only available to approved partners.',
    },
    [OPERATOR_CONNECT_CONFERENCING]: {
        serviceType: OPERATOR_CONNECT_CONFERENCING,
        serviceName: 'Operator Connect Conferencing',
        icon: occLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Enable Operator Connect Conferencing connectivity. This requires a valid Operator Connect service to be deployed.',
    },
    [DIRECT_ROUTING]: {
        serviceType: DIRECT_ROUTING,
        serviceName: 'Direct Routing',
        icon: msTeamsLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize TCAP access to your Microsoft 365 tenancy. Requires a global administrator account to authorize the application.',
    },
    [ZOOM]: {
        serviceType: ZOOM,
        serviceName: 'Zoom',
        icon: zoomIcon,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize your Zoom service to connect using Zoom Provider Exchange and activate Zoom Phone services',
    },
    [ZOOM_AUTH]: {
        serviceType: ZOOM_AUTH,
        serviceName: 'Zoom App Auth',
        icon: zoomIcon,
    },
    [EIGHTBYEIGHT]: {
        serviceType: EIGHTBYEIGHT,
        serviceName: '8x8',
        icon: eightByEightLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Create a new 8x8 tenant to provision users, manage licenses, and configure calling services',
    },
    [EIGHTBYEIGHT_AUTH]: {
        serviceType: EIGHTBYEIGHT_AUTH,
        serviceName: '8x8 Auth',
        icon: eightByEightLogo,
    },
    [SBC]: {
        serviceType: SBC,
        serviceName: SBCAAS_CLASSIC_LABEL,
        icon: sbcLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Connect SIP services to your company. This enables you to connect legacy and other cloud-based SIP platforms.',
    },
    [SRT]: {
        serviceType: SRT,
        serviceName: SBCAAS_MODERN_LABEL,
        icon: SRTLogo,
        addType: UCAAS_ACCORDION,
        addDescription: `Seamlessly bridges your existing legacy infrastructure with modern cloud-based SIP platforms as an alternative to ${SBCAAS_CLASSIC_LABEL}.`,
    },
    [TPM]: {
        serviceType: TPM,
        serviceName: 'Teams Phone Mobile',
        icon: teamsPhoneLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Enable Teams Phone Mobile connectivity. This requires a valid Operator Connect service to be deployed.',
    },
    [OPERATOR_PLATFORM]: {
        serviceType: OPERATOR_PLATFORM,
        addType: OPERATOR_PLATFORM_ACCORDION,
        addDescription: 'Add Operator Platform to Company',
    },
    [VENDOR_DEFINED]: {
        serviceType: VENDOR_DEFINED,
        addType: VENDOR_DEFINED_ACCORDION,
    },
    [GCC_HIGH]: {
        serviceType: GCC_HIGH,
        serviceName: 'GCC High Direct Routing',
        icon: GCCHighLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize TCAP access to your GCC High tenancy and configure Direct Routing services. Requires a global administrator account to authorize the application.',
    },
    [WEBEX]: {
        serviceType: WEBEX,
        serviceName: 'Webex Calling',
        icon: WebEx_Circle,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize your Webex tenant to activate Webex Calling services, with fully automated location and trunk management.',
    },
    [WEBEX_AUTH]: {
        serviceType: WEBEX_AUTH,
        serviceName: 'Webex App Auth',
        icon: WebEx_Circle,
    },
    [RINGCENTRAL]: {
        serviceType: RINGCENTRAL,
        serviceName: 'Ring Central',
        icon: ringCentralLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize your RingCentral service to connect using BYOC. Fully automate number and user management.',
    },
    [RINGCENTRAL_AUTH]: {
        serviceType: RINGCENTRAL_AUTH,
        serviceName: 'Ring Central App Auth',
        icon: ringCentralLogo,
    },
    [DIALPAD]: {
        serviceType: DIALPAD,
        serviceName: 'Dial Pad',
        icon: dialPadLogo,
        addType: UCAAS_ACCORDION,
        addDescription:
            'Authorize your Dial Pad service to connect using BYOC. Fully automate number and user management.',
    },
    [DIALPAD_AUTH]: {
        serviceType: DIALPAD_AUTH,
        serviceName: 'Dial Pad App Auth',
        icon: dialPadLogo,
    },
};

const UNKNOWN_CARD_DETAILS = {
    serviceType: 'Unknown',
    serviceName: 'Unknown Service',
};

const CardDetails = (serviceType, platformID) => {
    // Both Microsoft-tenant-family platforms share serviceType MICROSOFT_GRAPH;
    // disambiguate by platformID before falling back to the lookup table.
    if (serviceType === MICROSOFT_GRAPH) {
        return platformID === 'b8369b29-823d-4b3d-a12f-4f708993d0a8'
            ? {
                  serviceType: MICROSOFT_GRAPH,
                  serviceName: 'GCC High App Auth',
                  icon: GCCHighLogo,
              }
            : {
                  serviceType: MICROSOFT_GRAPH,
                  serviceName: 'Microsoft Graph API',
                  icon: MsAppAuthLogo,
              };
    }

    if (
        serviceType === VENDOR_DEFINED &&
        platformID === PlatformEnums.OPERATOR_PLATFORM
    ) {
        return {
            serviceType: OPERATOR_PLATFORM,
            addType: OPERATOR_PLATFORM_ACCORDION,
        };
    }

    return CARD_DETAILS_BY_SERVICE_TYPE[serviceType] ?? UNKNOWN_CARD_DETAILS;
};

export default CardDetails;
